/**
 * AllTxScreen — full-screen list of every transaction in the active book.
 *
 * Pixel-faithful per `design/project/screens-calendar.jsx` (calendar) and
 * `design/project/screens-home.jsx` (list rows).
 *
 * Layout (top → bottom):
 *   - Header bar — back arrow + centered 「全部紀錄」 title
 *   - Month picker — ◀ "2026 年 4 月" ▶ + "本月" sub-label when current
 *   - Totals — 支出 / 收入 cards (FX-converted into primary currency)
 *   - View toggle — 清單 / 月曆
 *   - List-only filter bar — 全部 / 支出 / 收入 + 依 日期 / 依 金額 sort
 *   - Body — list (RecentTxList) or calendar (CalendarView)
 *
 * Sharp edges:
 *   - Month math is **local time**. Using `t.date` lexicographic comparison
 *     against the month prefix `YYYY-MM-` is correct because we already
 *     enforce `IsoDate = YYYY-MM-DD` in `lib/sheets/types.ts`.
 *   - Totals FX-convert every row into the primary currency (the design
 *     specs that 支出 / 收入 are displayed as a single primary-currency
 *     figure; per-currency tabs live on the home BalanceCard).
 *   - List body's day grouping + sticky headers are owned by RecentTxList.
 */
import { useMemo, useState } from 'react';
import type { CategoryRow, TransactionRow } from '@/lib/sheets/types';
import { formatMonthLabel } from '@/lib/format/date';
import { formatAmount } from '@/lib/format/currency';
import { convertMinor } from '@/lib/format/fx';
import RecentTxList from '@/components/home/RecentTxList';
import CalendarView from './CalendarView';
import Icon from '@/components/common/Icon';
import type { KindFilter } from './searchFilters';

export type AllTxView = 'list' | 'calendar';
export type AllTxSort = 'date' | 'amount';

export interface AllTxScreenProps {
  transactions: TransactionRow[];
  categories: CategoryRow[];
  /** Primary display currency — totals are FX-converted into this and the
   *  child RecentTxList renders 「原幣」 sub-lines for foreign-currency rows. */
  primaryCurrency?: string;
  /** Optional initial cursor; defaults to "now". */
  initialCursor?: Date;
  /** Optional initial view; defaults to 'list'. */
  initialView?: AllTxView;
  onTxClick?: (tx: TransactionRow) => void;
  onClose?: () => void;
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function monthPrefix(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-`;
}

function shiftMonth(d: Date, delta: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + delta, 1);
}

function isSameMonthAsToday(d: Date): boolean {
  const t = new Date();
  return d.getFullYear() === t.getFullYear() && d.getMonth() === t.getMonth();
}

function ymdLocal(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function totalsInPrimary(
  txs: TransactionRow[],
  primary: string,
): { expense: number; income: number } {
  let expense = 0;
  let income = 0;
  for (const t of txs) {
    const minor = convertMinor(t.amount_minor, t.currency || 'TWD', primary);
    if (t.kind === 'income') income += minor;
    else if (t.kind === 'expense') expense += minor;
  }
  return { expense, income };
}

export default function AllTxScreen({
  transactions,
  categories,
  primaryCurrency,
  initialCursor,
  initialView = 'list',
  onTxClick,
  onClose,
}: AllTxScreenProps): JSX.Element {
  const [cursor, setCursor] = useState<Date>(
    () => initialCursor ?? new Date(),
  );
  const [view, setView] = useState<AllTxView>(initialView);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [kindFilter, setKindFilter] = useState<KindFilter>('all');
  const [sort, setSort] = useState<AllTxSort>('date');

  const primary = (primaryCurrency || 'TWD').toUpperCase();

  // Filter transactions to the active month + non-deleted.
  const monthlyAll = useMemo(() => {
    const prefix = monthPrefix(cursor);
    return transactions.filter(
      (t) => !t.deleted_at && t.date.startsWith(prefix),
    );
  }, [transactions, cursor]);

  // Totals reflect the entire month (untouched by the kind filter — design
  // shows them as fixed cards above the filter row).
  const totals = useMemo(
    () => totalsInPrimary(monthlyAll, primary),
    [monthlyAll, primary],
  );

  // Apply kind filter + sort for the list / calendar body.
  const monthly = useMemo(() => {
    const filtered =
      kindFilter === 'all'
        ? monthlyAll
        : monthlyAll.filter((t) => t.kind === kindFilter);
    if (sort === 'amount') {
      // Largest converted-amount first regardless of kind. Stable on date
      // tie-break so equal amounts keep their date order.
      return [...filtered].sort((a, b) => {
        const aMinor = convertMinor(
          a.amount_minor,
          a.currency || 'TWD',
          primary,
        );
        const bMinor = convertMinor(
          b.amount_minor,
          b.currency || 'TWD',
          primary,
        );
        if (aMinor !== bMinor) return bMinor - aMinor;
        return a.date < b.date ? 1 : a.date > b.date ? -1 : 0;
      });
    }
    return [...filtered].sort((a, b) =>
      a.date < b.date ? 1 : a.date > b.date ? -1 : 0,
    );
  }, [monthlyAll, kindFilter, sort, primary]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        background: 'var(--cream-50)',
      }}
    >
      {/* ── Header bar — back + centered title ──────────────── */}
      <div
        style={{
          padding: '14px 16px 6px',
          display: 'grid',
          gridTemplateColumns: '34px 1fr 34px',
          alignItems: 'center',
          flexShrink: 0,
        }}
      >
        {onClose ? (
          <button
            aria-label="返回"
            onClick={onClose}
            style={{
              width: 34,
              height: 34,
              borderRadius: 17,
              background: 'transparent',
              border: 0,
              cursor: 'pointer',
              display: 'grid',
              placeItems: 'center',
            }}
          >
            <Icon name="chevL" size={20} stroke="var(--fg-1)" />
          </button>
        ) : (
          <span />
        )}
        <div
          style={{
            font: '600 16px var(--font-cjk-display)',
            color: 'var(--fg-1)',
            textAlign: 'center',
          }}
        >
          全部紀錄
        </div>
        <span />
      </div>

      {/* ── Month picker — chevrons + 「2026 年 4 月」 + 「本月」 ─── */}
      <div
        style={{
          padding: '10px 16px 6px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 12,
          flexShrink: 0,
        }}
      >
        <button
          aria-label="上個月"
          onClick={() => setCursor((c) => shiftMonth(c, -1))}
          style={chevButton}
        >
          <Icon name="chevL" size={16} stroke="var(--fg-2)" />
        </button>
        <div style={{ textAlign: 'center', minWidth: 96 }}>
          <div
            style={{
              font: '600 18px var(--font-cjk-display)',
              color: 'var(--fg-1)',
              letterSpacing: '0.02em',
            }}
          >
            {formatMonthLabel(cursor)}
          </div>
          <div
            style={{
              font: '400 12px var(--font-cjk)',
              color: 'var(--fg-3)',
              marginTop: 2,
            }}
          >
            {isSameMonthAsToday(cursor) ? '本月' : ' '}
          </div>
        </div>
        <button
          aria-label="下個月"
          onClick={() => setCursor((c) => shiftMonth(c, 1))}
          style={chevButton}
        >
          <Icon name="chevR" size={16} stroke="var(--fg-2)" />
        </button>
        {!isSameMonthAsToday(cursor) && (
          <button
            type="button"
            data-testid="today-reset"
            aria-label="回到本月"
            onClick={() => {
              const now = new Date();
              setCursor(new Date(now.getFullYear(), now.getMonth(), 1));
              setSelectedDate(ymdLocal(now));
            }}
            style={{
              font: '500 12px var(--font-cjk)',
              color: 'var(--fg-2)',
              background: 'var(--bg-raised)',
              padding: '5px 10px',
              borderRadius: 999,
              border: 0,
              cursor: 'pointer',
            }}
          >
            今天
          </button>
        )}
      </div>

      {/* ── 支出 / 收入 cards ─────────────────────────────────── */}
      <div
        style={{
          padding: '8px 16px 6px',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 12,
          flexShrink: 0,
        }}
      >
        <div style={summaryCard}>
          <div style={summaryLabel}>支出</div>
          <div style={summaryValue}>
            {formatAmount(totals.expense, primary)}
          </div>
        </div>
        <div style={summaryCard}>
          <div style={summaryLabel}>收入</div>
          <div
            style={{
              ...summaryValue,
              color: 'var(--income)',
            }}
          >
            +{formatAmount(totals.income, primary)}
          </div>
        </div>
      </div>

      {/* ── List / Calendar toggle ────────────────────────────── */}
      <div style={{ padding: '6px 16px 4px', flexShrink: 0 }}>
        <div
          role="tablist"
          aria-label="檢視模式"
          style={{
            display: 'inline-flex',
            background: 'var(--bg-raised)',
            padding: 3,
            borderRadius: 999,
            font: '500 13px var(--font-cjk)',
          }}
        >
          {(
            [
              ['list', '清單'],
              ['calendar', '月曆'],
            ] as [AllTxView, string][]
          ).map(([v, label]) => {
            const on = view === v;
            return (
              <button
                key={v}
                role="tab"
                data-testid={`view-${v}`}
                aria-selected={on}
                onClick={() => setView(v)}
                style={{
                  border: 0,
                  background: on ? 'var(--bg-surface)' : 'transparent',
                  padding: '6px 16px',
                  borderRadius: 999,
                  color: on ? 'var(--fg-1)' : 'var(--fg-2)',
                  cursor: 'pointer',
                  font: 'inherit',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  boxShadow: on ? 'var(--shadow-1)' : 'none',
                }}
              >
                <Icon
                  name={v === 'list' ? 'note' : 'calendar'}
                  size={14}
                  stroke={on ? 'var(--fg-1)' : 'var(--fg-2)'}
                />
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Filter row (list mode only) — kind segmented + sort chip ── */}
      {view === 'list' && (
        <div
          style={{
            padding: '6px 16px 10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            flexShrink: 0,
          }}
        >
          <div
            role="tablist"
            aria-label="收支類型"
            style={{
              display: 'inline-flex',
              background: 'var(--bg-raised)',
              padding: 3,
              borderRadius: 999,
              font: '500 12px var(--font-cjk)',
            }}
          >
            {(
              [
                ['all', '全部'],
                ['expense', '支出'],
                ['income', '收入'],
              ] as [KindFilter, string][]
            ).map(([k, label]) => {
              const on = kindFilter === k;
              return (
                <button
                  key={k}
                  type="button"
                  role="tab"
                  data-testid={`kind-${k}`}
                  aria-selected={on}
                  onClick={() => setKindFilter(k)}
                  style={{
                    border: 0,
                    background: on ? 'var(--bg-surface)' : 'transparent',
                    padding: '5px 14px',
                    borderRadius: 999,
                    color: on ? 'var(--fg-1)' : 'var(--fg-2)',
                    cursor: 'pointer',
                    font: 'inherit',
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>
          <button
            type="button"
            data-testid="sort-toggle"
            aria-label="切換排序"
            onClick={() => setSort((s) => (s === 'date' ? 'amount' : 'date'))}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '6px 12px',
              borderRadius: 999,
              border: '0.5px solid var(--border-1)',
              background: 'var(--bg-surface)',
              color: 'var(--fg-2)',
              font: '500 12px var(--font-cjk)',
              cursor: 'pointer',
            }}
          >
            <Icon name="filter" size={13} stroke="var(--fg-2)" />
            依 {sort === 'date' ? '日期' : '金額'}
          </button>
        </div>
      )}

      {/* ── Body ──────────────────────────────────────────────── */}
      <div style={{ flex: 1, overflow: 'auto', padding: '4px 16px 24px' }}>
        {view === 'list' ? (
          monthly.length === 0 ? (
            <EmptyMonth />
          ) : (
            <RecentTxList
              transactions={monthly}
              categories={categories}
              {...(primaryCurrency ? { primaryCurrency } : {})}
              onTxClick={onTxClick}
            />
          )
        ) : (
          <CalendarView
            cursor={cursor}
            transactions={monthly}
            categories={categories}
            {...(primaryCurrency ? { primaryCurrency } : {})}
            selectedDate={selectedDate}
            onSelectDate={setSelectedDate}
            onTxClick={onTxClick}
          />
        )}
      </div>
    </div>
  );
}

function EmptyMonth(): JSX.Element {
  return (
    <div
      style={{
        padding: '40px 20px',
        textAlign: 'center',
        color: 'var(--fg-3)',
        font: '500 14px var(--font-cjk)',
      }}
    >
      這個月還沒有紀錄
    </div>
  );
}

const chevButton: React.CSSProperties = {
  width: 30,
  height: 30,
  borderRadius: 15,
  background: 'var(--bg-surface)',
  border: '0.5px solid var(--border-1)',
  cursor: 'pointer',
  display: 'grid',
  placeItems: 'center',
};

const summaryCard: React.CSSProperties = {
  padding: '12px 14px',
  borderRadius: 14,
  border: '0.5px solid var(--border-1)',
  background: 'var(--bg-surface)',
};

const summaryLabel: React.CSSProperties = {
  font: '500 11px var(--font-cjk)',
  color: 'var(--fg-3)',
  letterSpacing: '0.06em',
  marginBottom: 4,
};

const summaryValue: React.CSSProperties = {
  // clamp keeps the amount on one line on ≤390px viewports (iPhone 12 Pro
  // and narrower) where a 12-char +NT$ figure at 22px wraps into the
  // ~145px inner width of the 2-column grid card. Mirrors the pattern
  // already used in `BalanceCard.tsx`.
  font: '700 clamp(16px, 5vw, 22px) var(--font-num)',
  fontVariantNumeric: 'tabular-nums',
  color: 'var(--fg-1)',
  letterSpacing: '-0.01em',
};
